/**
 * KaisoAgentPanel — Agente operacional do restaurante.
 *
 * Flutua no canto inferior direito do dashboard.
 * O operador conversa em linguagem natural; o agente consulta dados reais
 * e executa ações com base nos tools do backend.
 */
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Bot, X, Send, Loader2, Trash2, ChevronDown } from "lucide-react";

const SUGGESTED_PROMPTS = [
  "Quais mesas estão com pedidos pendentes?",
  "Qual é o resumo de vendas de hoje?",
  "Tem pedido aguardando pagamento?",
  "O caixa está aberto?",
];

function AgentMessage({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-2`}>
      {!isUser && (
        <div className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
          <Bot className="w-3.5 h-3.5 text-white" />
        </div>
      )}
      <div
        className={`max-w-[80%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-emerald-600 text-white rounded-br-sm"
            : "bg-gray-100 text-gray-800 rounded-bl-sm"
        }`}
      >
        {msg.content}
        {msg.actions?.length > 0 && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-200 space-y-0.5">
            {msg.actions.map((a, i) => (
              <p key={i} className="text-xs text-gray-500 italic">
                ⚙ {a.tool.replace(/_/g, " ")}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function KaisoAgentPanel() {
  const { authAxios } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Olá! Sou o KaisoAgent. Pergunta-me sobre pedidos, mesas, caixa ou vendas de hoje." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setLoading(true);

    try {
      const res = await authAxios.post("/kaiso-agent/chat", {
        message: msg,
        session_id: sessionId,
      });
      const data = res.data;
      if (!sessionId) setSessionId(data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply, actions: data.actions },
      ]);
    } catch (err) {
      const detail = err.response?.data?.detail || "Erro ao contactar o agente.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `⚠ ${detail}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const clearSession = async () => {
    if (sessionId) {
      try { await authAxios.delete(`/kaiso-agent/session/${sessionId}`); } catch {}
    }
    setSessionId(null);
    setMessages([{ role: "assistant", content: "Sessão reiniciada. Como posso ajudar?" }]);
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[60] w-12 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 shadow-lg flex items-center justify-center transition-all"
          title="KaisoAgent — assistente operacional"
          data-testid="kaiso-agent-btn"
        >
          <Bot className="w-6 h-6 text-white" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-[60] w-80 sm:w-96 flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
          style={{ height: "520px" }}
          data-testid="kaiso-agent-panel"
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-emerald-600 text-white">
            <Bot className="w-5 h-5" />
            <span className="font-bold text-sm flex-1">KaisoAgent</span>
            <button onClick={clearSession} title="Limpar conversa" className="p-1 rounded hover:bg-emerald-700 transition">
              <Trash2 className="w-4 h-4" />
            </button>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-emerald-700 transition">
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
            {messages.map((msg, i) => (
              <AgentMessage key={i} msg={msg} />
            ))}
            {loading && (
              <div className="flex justify-start mb-2">
                <div className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center mr-2 flex-shrink-0">
                  <Bot className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-gray-100 rounded-xl rounded-bl-sm px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Suggested prompts — show only on first interaction */}
          {messages.length === 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {SUGGESTED_PROMPTS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(p)}
                  className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-1 hover:bg-emerald-100 transition"
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t border-gray-100 px-3 py-2 flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
              }}
              placeholder="Pergunta sobre pedidos, mesas, caixa..."
              rows={1}
              className="flex-1 resize-none text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 max-h-24 overflow-auto"
              style={{ minHeight: "38px" }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-40 transition flex-shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
